import { reactive, ref, computed, watch, onBeforeMount } from '@vue/composition-api'
import { formatDate, commonErrorMessage } from '@/util/common'
import * as _ from 'lodash'
import * as apiNote from '@/api/note'
import { showNoteWarningDialog } from '@/logic/note/NoteWarningDialog'

export const NoteEditPart = (props, context) => {

  const store = context.root.$store
  const router = context.root.$router

  /**
   * ノートトップに戻る
   */
  const backToNoteTop = () => {
    store.dispatch('note/forceMove') 
    store.dispatch('note/modeChange', 'detail') 
    store.dispatch('note/setCurrentId', null) 
    if (store.state.common.device === 'sp') {
      // SPの場合は、ノート一覧画面に遷移
      router.push({ name: 'SPNoteList'})
    } else {
      // PCの場合は、親画面の全体のリロード
      action.loadPage()
    }
  }

  // エラー時に要素にスクロールするためref使用
  const titleRef = ref(null)
  // バリデート要素
  const validate = reactive({
    title: '',
    error: (error) => {
      const dialogType = error.data.dialog_type ?? ''
      if (dialogType !== '') {
        // 特殊なダイアログボックスを表示する場合

        let button1Callback = null
        let button2Callback = null
        const options = {}
        
        if (dialogType === 'duplicateEdit') {
          // 重複編集の場合

          // ダイアログに表示する編集した他ユーザー名を設定
          const otherUsers = error.data.other_users ?? []
          const otherUserNames = []
          for (let i = 0; i < otherUsers.length; i++) {
            otherUserNames.push(otherUsers[i].name)
          }
          options.userName = otherUserNames.join('、')

          // 「上書きする」ボタンを押した場合の処理
          button2Callback = () => {
            // 上書き実行フラグをtrueに設定
            store.dispatch('note/setOverwrite', true)
            // ノート更新
            listAction.update()
              .then(res => {
                backToNoteTop()
              })
          }
        } else if (dialogType === 'noteHasDeleted') {
          // 編集中にノート所有者がノートを削除した場合

          // 「ノートトップに戻る」ボタンを押下した場合の処理
          button1Callback = () => {
            backToNoteTop()
          }
        }

        showNoteWarningDialog(dialogType, 'edit', button1Callback, button2Callback, options)
      } else {
        // 通常のダイアログボックス
        const {
          data: {
            error_messages: errMsgs
          }
        } = error

        if (errMsgs && errMsgs.title) {
          validate.title = _.isArray(errMsgs.title) ? errMsgs.title.join(' ') : errMsgs.title
          titleRef.value.focus()
        } else {
          commonErrorMessage(error)
        }
      }
    },
    success: () => {
      validate.title = ''
    }
  })

  // utilのロジックが削除されていたため
  const historyBack = () => {
    router.go(-1)
  }

  store.dispatch('note/getTags')

  // 変更差分をとるため、変更前の情報を保持
  const detailOld = computed(() => {
    return store.getters['note/detail']
  })

  const detail = computed(() => {
    return store.getters['note/detailEdit']
  })

  const currentId = computed(() => store.getters['note/currentId'])

  const share = reactive({
    /**
     * 選択している権限付与アカウントの数を取得
     * ※権限付与アカウントは、ノート作成者しか設定をできない。
     * ※権限付与アカウントリストの中には、ノート作成者含まれない。
     *   (ノート利用者権限更新(NT/013)の処理でノート作成者は登録しないように対応しているので保障されている)
     * ※このメソッドは共有設定エリアを表示する時にしか使われないので、ノート作成者の場合しか使われない。
     */
    getShareUsersCount: (shareType, shareClassification) => {
      if (shareType === 2) {
        if (shareClassification === 21) {
          const shareUsersViewable = store.getters['note/shareUsersViewable']
          return shareUsersViewable.length
        } else if (shareClassification === 22) {
          const shareUsersEditable = store.getters['note/shareUsersEditable']
          return shareUsersEditable.length
        }
      }
      return 0
    },
    /**
     * （共有の場合）選択されている権限付与アカウントは存在するか？
     * ※権限付与アカウントは、ノート作成者しか設定をできない。
     * ※権限付与アカウントリストの中には、ノート作成者含まれない。
     *   (ノート利用者権限更新(NT/013)の処理でノート作成者は登録しないように対応しているので保障されている)
     * ※このメソッドは共有設定エリアを表示する時にしか使われないので、ノート作成者の場合しか使われない。
     */ 
    isUsersToShareSelected: computed(() => {
      const shareType = store.state.note.detailEdit.share_type
      const shareClassification = store.state.note.detailEdit.share_classification
      const shareUsersViewable = store.getters['note/shareUsersViewable']
      const shareUsersEditable = store.getters['note/shareUsersEditable']

      if (shareType === 2) {
        // 「共有」にチェックされている場合
        if (shareClassification === 21 && shareUsersViewable.length > 0) {
          // 閲覧可を選択し、閲覧可能なアカウントが存在する場合
          return true
        } else if (shareClassification === 22 && shareUsersEditable.length > 0) {
          // 編集可を選択し、編集可能なアカウントが存在する場合
          return true
        }
      }
      return false
    }),
    /**
     * 共有タイプ(プライベート/共有)のラジオボタンが切り替わった場合
     * ※このメソッドは共有設定エリアを表示する時にしか使われないので、ノート作成者の場合しか使われない。
     */
    onShareTypeChanged: () => {
      const shareType = store.state.note.detailEdit.share_type
      const shareClassification = store.state.note.detailEdit.share_classification
      const shareUsersCount = share.getShareUsersCount(2, shareClassification)
      if (shareClassification && shareUsersCount > 0 && shareType === 1) {
        // 共有区分が選択された状態で、共有→プライベートに変更された場合はダイアログを表示
        // (共有区分が未選択で、共有→プライベートに変更した場合は表示しない)
        showNoteWarningDialog('shareTypeToPrivate', 'edit')
      }
    },
    /**
     * 共有区分のラジオボタンor項目名をクリックした場合
     * ※このメソッドは共有設定エリアを表示する時にしか使われないので、ノート作成者の場合しか使われない。
     * @param newShareClassification 対象の共有区分 (@changeではなく@clickで実行するので共有区分を引数で渡している)
     */
    onShareClassificationClicked: (newShareClassification, isRadio) => {

      // 変更前の共有状態
      const prevShareClassification = store.state.note.detailEdit.share_classification
      const prevShareUsersCount = share.getShareUsersCount(2, prevShareClassification)
      
      if (prevShareClassification && prevShareUsersCount > 0 && newShareClassification !== prevShareClassification) {
        // 共有区分が選択された状態で、共有区分を切り替えた場合

        // 「閉じる」ボタン押下した場合
        const button1Callback = () => {
          // ラジオボタンのチェックを変更前の共有状態に戻す
          store.dispatch('note/setDetailEditValue', { key: 'share_classification', value: prevShareClassification })
        }

        // 「アカウントを指定する」ボタンを押下した場合
        const button2Callback = () => {
          // 次の処理に進む
          share.onShareClassificationClickedStep2(newShareClassification, isRadio)
        }

        // 共有区分切り替え確認ダイアログを表示
        showNoteWarningDialog('changeShareClassification', 'edit', button1Callback, button2Callback)
      } else {
        // 次の処理に進む
        share.onShareClassificationClickedStep2(newShareClassification, isRadio)
      }
    },
    onShareClassificationClickedStep2: (newShareClassification, isRadio) => {
      
      store.dispatch('note/setDetailEditValue', { key: 'share_classification', value: newShareClassification })

      // 閲覧/編集権限付与設定ウィンドウを表示
      store.dispatch('noteUserPermissionsEdit/show', 'edit')
    }
  })

  const tags = reactive({
    newTag: '',
    all: computed(() => store.getters['note/tagLists']),
    history: computed(() => {
      const diff = _.difference(_.map(tags.all, 'name'), _.map(detail.value.tags, 'name'))
      return _.filter(tags.all, tag => diff.includes(tag.name))

      // return _.difference(tags.all, detail.value.tags)
    }),
    isHistory: computed(() => tags.history.length > 0),
    addTag: () => {
      const tagName = tags.newTag.trim()
      if (tagName === '') {
        tags.newTag = ''
        return
      }
      // 同名タグがすでに設定されている場合は入力欄をクリアして終了
      if (_.map(detail.value.tags, 'name').includes(tagName)) {
        tags.newTag = ''
        return
      }
      detail.value.tags.push({
        id: null,
        name: tagName,
        count: 0
      })
      tags.newTag = ''
    },
    addEditTag: (addTag) => {
      detail.value.tags.push(addTag)
    },
    removeEditTag: (removeTag) => {
      const tagsIndex = _.findIndex(detail.value.tags, tag => tag.name === removeTag.name)
      detail.value.tags.splice(tagsIndex, 1)
    }
  })

  const state = reactive({
    profileId: computed(() => store.state.profile.id), // 自分(ログインユーザー)のID
    // 新規作成か判定
    isCreate: computed(() => {
      return store.state.note.mode === 'create'
    }),
    // 編集か判定
    isEdit: computed(() => {
      return store.state.note.mode === 'edit'
    }),
    // 共有エリア表示フラグ
    isShareAreaShown: computed(() => {
      // 新規作成で、共有できるアカウントが存在する場合、
      // または、編集で、自分が所有者、かつ共有できるアカウントが存在する場合
      // に共有エリアを表示する
      const userPermissions = store.getters['note/userPermissions']
      const otherUserPermissions = _.reject(userPermissions, list => {
        return list.id === state.profileId
      })
      const otherUserPermissionsCount = otherUserPermissions.length
      return (state.isCreate && otherUserPermissionsCount > 0) || 
        (state.isEdit && store.getters['note/isOwn'] && otherUserPermissionsCount > 0)
    }),
    // タグエリア表示フラグ
    isTagAreaShown: computed(() => {
      // 新規作成の場合、または、編集の場合で自分が所有者である場合のみ表示
      return state.isCreate || (state.isEdit && store.getters['note/isOwn'])
    }),
    // ロード中フラグ
    isLoadingDetail: computed(() => store.state.note.loading.detail), 
    isSaving: false, // 保存処理中フラグ
    isOpenHistoryTags: false,
    isChanged: false,
    targetListIndex: null,
    targetListLinkId: null,
    targetListContent: null,
    targetListType: null,
    targetNoteRowId: null,
    visibleNotePartAdd: false,
    visibleNoteSelect: false,
    visibleNoteCreate: false,
    visibleNoteChangePosition: false,
    visibleNotePartDeleteConfirm: false,
    visibleNotePartPreventForgettingToSave: computed(() => store.getters['note/checkForgetToSave'])
  })

  const partsDefault = {
    text: {
      'id': null,
      'type': 0,
      'rank': null,
      'content': ''
    },
    textarea: {
      'id': null,
      'type': 1,
      'rank': null,
      'content': ''
    }
  }

  const action = reactive({
    /**
     * 画面全体のリロード
     */
    loadPage: () => {
      context.emit('loadPage')
    }
  })

  // 見出し、コメント等の移動、コピーなど
  const listAction = reactive({
    add: {
      open: (index) => {
        state.visibleNotePartAdd = true
        state.targetListIndex = index
      },
      close: () => {
        state.visibleNotePartAdd = false
      },
      addText: () => {
        state.visibleNotePartAdd = false
        detail.value.lists.splice(state.targetListIndex + 1, 0, _.clone(partsDefault.text))
      },
      addTextarea: () => {
        state.visibleNotePartAdd = false
        detail.value.lists.splice(state.targetListIndex + 1, 0, _.clone(partsDefault.textarea))
      }
    },
    copy: {
      open: (index) => {
        state.targetListIndex = index
        const own = detail.value.lists[state.targetListIndex]
        if (own.type === 0) {
          // ノート行(テキスト)の「別のノートの複製」の場合
          state.targetListType = 'text'
          state.targetListContent = own.content
        } else if (own.type === 1) {
          // ノート行(テキストエリア)の「別のノートの複製」の場合
          state.targetListType = 'textarea'
          state.targetListContent = own.content
        } else if (own.type === 2) {
          // 最新ナレッジの場合
          state.targetListType = 'knowledge'
          state.targetListLinkId = own.knowledge.id
          state.targetListContent = own.knowledge.name
          state.targetNoteRowId = own.id
        } else if (own.type === 3) {
          // QAの場合
          state.targetListType = 'qa'
          state.targetListLinkId = own.qa.id
          state.targetListContent = own.qa.name
          state.targetNoteRowId = own.id
        } else if (own.type === 4) {
          // 資料（先例・判例）の場合
          state.targetListType = 'pj'
          state.targetListLinkId = own.pj.id
          state.targetListContent = own.pj.name
          state.targetNoteRowId = own.id
        }
        state.visibleNoteSelect = true
        store.dispatch('note/spShowPop', true)
      },
      close: () => {
        state.visibleNoteSelect = false
        store.dispatch('note/spShowPop', false)
      }
    },
    createnote: {
      open: () => {
        state.visibleNoteSelect = false
        state.visibleNoteCreate = true
        store.dispatch('note/spShowPop', true)
      },
      close: () => {
        state.visibleNoteCreate = false
        store.dispatch('note/spShowPop', false)
      }
    },
    move: {
      open: (index) => {
        state.targetListIndex = index
        state.visibleNoteChangePosition = true
      },
      close: () => {
        state.visibleNoteChangePosition = false
      },
      move: (index) => {
        state.visibleNoteChangePosition = false
        const own = detail.value.lists[state.targetListIndex]
        detail.value.lists.splice(state.targetListIndex, 1)
        detail.value.lists.splice(index, 0, own)
      }
    },
    drag: {
      end: (e) => {
      }
    },
    delete: {
      open: (index) => {
        state.visibleNotePartDeleteConfirm = true
        state.targetListIndex = index
        const own = detail.value.lists[state.targetListIndex]
        if (own.type === 0) {
          state.targetListType = 'text'
        } else if (own.type === 1) {
          state.targetListType = 'textarea'
        } else if (own.type === 2) {
          state.targetListType = 'knowledge'
        } else if (own.type === 3) {
          state.targetListType = 'qa'
        } else if (own.type === 4) {
          state.targetListType = 'pj'
        }
      },
      close: () => {
        state.visibleNotePartDeleteConfirm = false
      },
      delete: () => {
        state.visibleNotePartDeleteConfirm = false
        detail.value.lists.splice(state.targetListIndex, 1)
      }
    },
    /**
     * 入力データの組み合わせが正しいかチェックする
     */ 
    isInputDataValid: computed(() => {
      const title = detail.value.title
      const shareType = detail.value.share_type
      const shareUsers = detail.value.share_users

      // 初期化
      let isOK = true
      listAction.errorMessageOfTitle = ''

      // タイトル未入力チェック
      if (_.isEmpty(title)) {
        listAction.errorMessageOfTitle = 'タイトルを入力して下さい。'
        isOK = false
      }

      // 共有タイプ、共有区分の組み合わせが正しいかチェックする
      if (shareType === 1) {
        // Nothing to do.
      } else if (shareType === 2) {
        if (shareUsers.length <= 0) {
          isOK = false
        } else {
          for (let i = 0; i < shareUsers.length; i++) {
            const userId = shareUsers[i].id
            const shareClassification = shareUsers[i].type
            if (!(_.isInteger(userId) && userId > 0 && (shareClassification === 21 || shareClassification === 22))) {
              // ユーザーID、または共有区分の値が正しくない
              isOK = false
              break
            } 
          }
        }
      } else {
        // share_typeの値が正しくない（プログラムのバグ）
        isOK = false
      }

      return isOK
    }),
    // タイトルのエラーメッセージ
    errorMessageOfTitle: '',
    // 新規作成
    create: async () => {
      // console.log('### NoteEditPart.create() ###')

      const shareType = detail.value.share_type
      const shareUsers = detail.value.share_users

      // ノート追加APIをコール
      const params = {
        title: detail.value.title,
        share_type: shareType,
        tags: _.map(detail.value.tags, 'name')
      }

      const res = await apiNote
        .add(params)
        .then(res => {
          const { data } = res
          if (data.status === 0) {
            const noteId = data.id
            store.dispatch('note/setCurrentId', noteId)

            if (shareType === 2) {
              // 共有の場合
              // ノート利用者権限を更新
              listAction.updateUserPermissions(noteId, shareUsers)
            }
          } else {
            throw res
          }
        })
        .catch(error => {
          return Promise.reject(error)
        })

      return res
    },
    // 更新
    update: async () => {
      // console.log('### NoteEditPart.update() ###')

      // ノート更新
      const noteId = currentId.value
      const shareType = detail.value.share_type
      const shareUsers = detail.value.share_users

      const params = {
        id: noteId,
        title: detail.value.title,
        share_type: shareType,
        tags: _.map(detail.value.tags, 'name'),
        note_rows: _.map(detail.value.lists, list => {
          const save = {
            type: list.type
          }

          // IDを持っている場合はAPI側に渡す（=行の更新）
          if (list.id) {
            save.id = list.id
          }
          save.text = (() => {
            switch (list.type) {
              case 0: return list.content
              case 1: return list.content
              case 2: return list.knowledge.id
              case 3: return list.qa.id
              case 4: return list.pj.id
            }
          })()

          return save
        })
      }

      // ノート編集開始日時
      params.edit_start_time = store.state.note.detailEdit.current_time

      // 上書き実行フラグ
      if (store.state.note.overwrite) {
        params.overwrite = 1
      }

      const res = await apiNote
        .update(params)
        .then(async (response) => {
          const { data } = response
          if (data.status === 0) {
            store.dispatch('note/reset')

            if (shareType === 2) {
              // 共有の場合
              // ノート利用者権限を更新
              response = await listAction.updateUserPermissions(noteId, shareUsers)
                .then((res2) => {
                  return res2
                })
                .catch(error => {
                  return Promise.reject(error)
                })
            }
          } else {
            // 特殊なダイアログボックスを表示する場合は、ここでダイアログをのタイプを設定しておく
            // （validate.error()メソッド内での場合分けに使用する）
            if (data.status === 423) {
              // 重複編集の場合
              response.data.dialog_type = 'duplicateEdit'
            } else if (data.status === 404) {
              // 編集中にノート所有者がノートを削除した場合
              response.data.dialog_type = 'noteHasDeleted'
            }
            throw response
          }

          return response
        })
        .catch(error => {
          return Promise.reject(error)
        })
        .finally(() => {
          // 上書き実行フラグを初期値に戻す
          store.dispatch('note/setOverwrite', false)
        })

      return res
    },
    /**
     * ノート利用者権限を更新
     */
    updateUserPermissions: async (noteId, shareUsers) => {

      // 【権限関係の変数および処理について】
      //
      // store.state.note.detail.share_usersは、ノート利用者権限更新API(NT-013)を読み込んだ編集開始時の権限の設定状態(id,type)を保持するリスト。
      // store.state.note.detailEdit.share_usersは、編集開始時はstore.state.note.detail.share_usersと同じ内容であるが、権限を変更することで変化する。
      // これらは、ノートを編集したかどうかのチェックのために使用する。
      // 
      // store.state.note.userPermissionsは、画面表示用のリストであり、id,type,nameのリスト。
      // store.state.note.shareUsersViewableは、閲覧権限を付与したid,typeのリスト。
      // store.state.note.shareUsersEditableは、編集権限を付与したid,typeのリスト。
      // このメソッドの引数shareUsersは、APIのリクエストパラメータshare_usersに設定するために加工されたid,typeのリスト。
      // shareUsersは、share_classification=21の場合は、shareUsersViewableの値が設定され、
      // share_classification=22の場合は、shareUsersEditableの値設定される。
      // 
      // 現行の仕様は、同じノートに対して、全アカウントとも閲覧可能、または編集可能のいずれかに統一されるという仕様である。
      // 開発時に、アカウント別に、閲覧可能、編集可能を設定できる仕様に変わる可能性も考えられたため、
      // 柔軟に対応できるよう、shareUsersViewable、shareUsersEditableの2つの変数を設けている。
      // 
      // DBのノート権限テーブル(user_permissions)には、ノートを共有する利用者IDのみ登録し、
      // ノート作成者を登録してはならない。
      // その前提であるため、もし、ノート作成者の利用者IDを登録すると、ノート編集時の現在の選択しているユーザー数に影響出ると思われる。
      // (例えば、編集モードの「保存」ボタンを非アクティブにする制御など)
      // 
      // もし、ノート利用者権限更新API(NT-013)のリクエストパラメータshare_usersに、
      // ノート作成者の利用者IDを含めてしまった場合は、API側でuser_permissionsテーブルに登録しないようにする対策はしている。
      //
      // ノート作成者でログイン時、編集モードを表示した場合は、共有エリアが表示され編集可能である。
      // 
      // ノート編集権限アカウントでログイン時、編集モードを表示した場合は、共有エリアは表示しないので、権限は変更不可能である。
      // ノート権限リストは、APIから取得し、変更せずに、リクエストパラメータshare_usersに設定してAPIにそのまま送信する（権限が変わらない）処理をしている。
      // また、共有エリアを表示しない前提であるため、
      // 閲覧/権限編集ウィンドウの処理や、現在選択している権限付与アカウント数のチェックなどにの処理では、
      // ノート編集権限アカウントでログイン時の場合分け対応は、不要であるためしていない。
      
      const shareUsersJson = JSON.stringify(shareUsers)

      // ノート利用者権限更新APIをコール
      const params = {
        id: noteId,
        share_users: shareUsersJson
      }

      const res = await apiNote
        .updateUserPermissions(params)
        .then(response => {
          const { data } = response
          if (data.status === 0) {
            return response
          } else {
            throw response
          }
        })

      return res
    },
    save: async () => {
      if (!currentId.value) {
        // 現在のノートIDがない場合は新規作成
        const res = await listAction
          .create()
          .then(async (r) => {
            // 行データ等の登録をするためupdate()を実行する
            const update = await listAction
              .update()
              .then(res => {
                const { data } = res
                if (data.status === 0) {
                  // 成功時は特になし
                  return res
                } else {
                  throw res
                }
              })
              .catch(error => {
                return Promise.reject(error)
              })

            return update
          })
          .catch(error => {
            return Promise.reject(error)
          })

        return res
      } else {
        // 現在のノートIDがある場合は更新
        const res = await listAction
          .update()
          .then(res => {
            const { data } = res
            if (data.status === 0) {
              // 成功時は特になし
              return res
            } else {
              throw res
            }
          })
          .catch(error => {
            return Promise.reject(error)
          })

        return res
      }
    },
    clickSave: () => {

      if (!listAction.isInputDataValid) {
        // 入力データが正しくない場合は、エラーメッセージを表示して終了
        validate.title = listAction.errorMessageOfTitle
        return
      }

      state.isSaving = true
      store.dispatch('common/showLoading')

      listAction
        .save()
        .then(() => {
          validate.success()
          store.dispatch('note/getDetail')
          store.dispatch('note/getTags')
          store.dispatch('note/getNoteCount')
          store.dispatch('note/getUserLists')
          store.dispatch('note/getNoteLists')
          store.dispatch('common/showToast', { text: 'ノートを保存しました' })

          store.dispatch('note/resetCheck')
          context.emit('finishEditMode')
        })
        .catch(error => {
          validate.error(error)
        })
        .finally(() => {
          state.isSaving = false
          store.dispatch('common/hideLoading')
        })
    },
    /**
     * 「ノートの編集の保存」ダイアログの各種ボタン押下時の処理
     */
    forget: {
      // ×ボタン押下
      close: () => {
        // ポップアップを閉じる
        store.dispatch('note/hideDialogToPreventForgettingToSave')
      },
      // 保存しないで移動
      move: () => {
        store.dispatch('note/forceMove')
        listAction.forget.moveFinally()
      },
      // 保存して移動
      saveAndMove: () => {

        if (!listAction.isInputDataValid) {
          // 入力データが正しくない場合は、エラーメッセージを表示して終了
          validate.title = listAction.errorMessageOfTitle
          store.dispatch('note/hideDialogToPreventForgettingToSave')
          // メニューが表示されている場合は閉じる
          context.emit('closeMenu')
          return
        }

        store.dispatch('common/showLoading')
        listAction
          .save()
          .then(() => {
            store.dispatch('common/showToast', { text: 'ノートを保存しました' })
            listAction.forget.moveFinally()
          })
          .catch(error => {
            store.dispatch('note/resetCheck')
            context.emit('closeMenu')
            validate.error(error)
          })
          .finally(() => {
            store.dispatch('common/hideLoading')
          })
      },
      // 「保存しないで移動」と「保存して移動」の共通処理
      moveFinally: () => {

        // 遷移先情報（store.dispatch('note/resetCheck')を実行するとクリアされるので、それよりも前に取得しておく）
        const linkTo = store.getters['note/linkTo']

        // 後処理（次回アクセスのための値のクリア処理）
        validate.title = ''
        store.dispatch('note/resetCheck')

        if (linkTo.type === 'route') {
          // linkTo.type = 'route'は、PCNoteList/SPNoteListのbeforeRouteLeave()で呼ばれる場合であり、
          // 画面遷移するケースである。
          router.push({ name: linkTo.to })
        } else if (linkTo.type === 'mode' && linkTo.to === 'search') {
          context.emit('finishEditMode')
          context.emit('search')
        } else if (linkTo.type === 'mode' && linkTo.to === 'user_checked') {
          context.emit('finishEditMode')
          context.emit('loadAfterUserChecked')
        } else if (linkTo.type === 'mode' && linkTo.to === 'tag_changed') {
          context.emit('finishEditMode')
          context.emit('loadAfterTagChanged')
        } else if (linkTo.type === 'mode' && linkTo.to === 'history_back') {
          context.emit('finishEditMode')
          store.dispatch('note/forceMove') 
          router.go(-1)
        } else {
          // その他は閲覧モードに切り替える
          context.emit('finishEditMode')
          if (store.state.common.device === 'sp') {
            context.emit('changeToDetailMode')
          }
          store.dispatch('note/getDetail')
          store.dispatch('note/getTags')
          store.dispatch('note/getNoteCount')
          store.dispatch('note/getUserLists')
          store.dispatch('note/getNoteLists')
        }
      }
    }
  })

  const handleScrollChild = (e) => {
    context.emit('scroll', e)
  }

  // ノートの編集状況を監視
  watch(
    () => detail.value,
    (now, prev) => {
      // 編集前（ロード時）の内容
      const prevObj = {
        title: detailOld.value.title,
        lists: detailOld.value.lists,
        shareType: detailOld.value.share_type,
        shareClassification: detailOld.value.share_classification,
        shareUsers: detailOld.value.share_users,
        tags: detailOld.value.tags
      }
      // 現在の内容
      const nowObj = {
        title: now.title,
        lists: now.lists,
        shareType: now.share_type,
        shareClassification: now.share_classification,
        shareUsers: now.share_users,
        tags: now.tags
      }
      // 編集されたか判定
      state.isChanged = !_.isEqual(prevObj, nowObj)
      store.dispatch('note/noteEdited', { id: currentId.value, edited: state.isChanged })
    },
    { deep: true }
  )


  return {
    titleRef,
    validate,
    detail,
    share,
    tags,
    state,
    action,
    listAction,
    formatDate,
    handleScrollChild,
    currentId,
    historyBack
  }
}
